import _ from 'lodash';
import APIClient from '../api';
import { fetchVariantsQueue, fetchPinsQueue } from '../api/queues';

export const GET_VARIANTS_QUEUE_SUCCESS = 'GET_VARIANTS_QUEUE_SUCCESS';
export const GET_PINS_QUEUE_SUCCESS = 'GET_PINS_QUEUE_SUCCESS';
export const REMOVE_PIN_FROM_QUEUE = 'REMOVE_PIN_FROM_QUEUE';

export const PINS_QUEUE_PAGE_SIZE = 25;
const PINS_QUEUE_REFETCH_BELOW = 5;

export const getVariantsQueue = (params = {}) => async dispatch => {
  const request = takeLatestQueueRequest('variants');
  let resp;
  try {
    resp = await fetchVariantsQueue(params, { signal: request.signal });
  } catch (err) {
    if (APIClient.isCancel(err)) return;
    throw err;
  }
  if (!request.isCurrent()) return;
  return dispatch({
    type: GET_VARIANTS_QUEUE_SUCCESS,
    variants: _.get(resp, 'variants', [])
  });
};

export const getPinsQueue = (params = {}) => async dispatch => {
  const request = takeLatestQueueRequest('pins');
  const pins = [];
  let resp, fetchedPins;
  // A lagging replica can fill the first page with pins just matched here; read one page further rather than show a near-empty queue.
  for (let page = 1; page <= 2; page++) {
    try {
      resp = await fetchPinsQueue({ ...params, page, pageSize: PINS_QUEUE_PAGE_SIZE }, { signal: request.signal });
    } catch (err) {
      if (APIClient.isCancel(err)) return;
      throw err;
    }
    if (!request.isCurrent()) return;
    fetchedPins = _.get(resp, 'pins', []);
    pins.push(...fetchedPins.filter(pin => !wasPinRemovedRecently(pin.id)));
    if (fetchedPins.length < PINS_QUEUE_PAGE_SIZE || pins.length >= PINS_QUEUE_REFETCH_BELOW) break;
  }
  return dispatch({
    type: GET_PINS_QUEUE_SUCCESS,
    pins: _.uniqBy(pins, 'id'),
    fetchedCount: fetchedPins.length,
    resultsMayBeIncomplete: !!_.get(resp, 'resultsMayBeIncomplete'),
    searchRequiresUsername: !!_.get(resp, 'searchRequiresUsername')
  });
};

// Drops a pin the admin just matched or orphaned without re-running the search. Resolves true when the
// visible list has run low and the last fetch was a full page, so the caller should re-fetch.
export const removePinFromQueue = Pin_id => async (dispatch, getState) => {
  rememberRemovedPins([Pin_id]);
  dispatch({ type: REMOVE_PIN_FROM_QUEUE, Pin_id });
  return isPinsQueueRunningLow(getState().queue);
};

// The pins queue reads from a replica that can lag the match write, so a re-fetch right after matching
// could hand back pins that were just matched; those stay hidden from results for a short window.
export const rememberRemovedPins = Pin_ids => Pin_ids.forEach(Pin_id => recentlyRemovedPinIds.set(Pin_id, Date.now()));
export const forgetRemovedPins = Pin_ids => Pin_ids.forEach(Pin_id => recentlyRemovedPinIds.delete(Pin_id));

const isPinsQueueRunningLow = queue => queue.pinTicketsMayHaveMore && queue.pinTickets.length < PINS_QUEUE_REFETCH_BELOW;

const RECENTLY_REMOVED_PIN_WINDOW_MS = 60 * 1000;
const recentlyRemovedPinIds = new Map();
const wasPinRemovedRecently = Pin_id => Date.now() - (recentlyRemovedPinIds.get(Pin_id) || 0) < RECENTLY_REMOVED_PIN_WINDOW_MS;

// Only the newest request per queue may write to the store — a slow, superseded search resolving
// late would otherwise snap the visible results back to a stale list. Aborting the superseded
// request also lets the backend kill its database query.
const latestQueueRequests = {
  variants: { seq: 0, controller: null },
  pins: { seq: 0, controller: null }
};

const takeLatestQueueRequest = queue => {
  const tracker = latestQueueRequests[queue];
  tracker.controller?.abort();
  tracker.controller = new AbortController();
  const seq = ++tracker.seq;
  return { signal: tracker.controller.signal, isCurrent: () => seq === tracker.seq };
};
